import React, { useState, useEffect, useRef } from "react";
import { string, bool, func, arrayOf, shape } from "prop-types";
import * as Yup from "yup";
import Drawer from "pages/_components/Drawer/Drawer";
import { Field, Form, Formik } from "formik";
import I18n from "pages/_components/I18n";
import Checkbox from "pages/_components/Checkbox";
import { DateRangeField } from "pages/_components/fields/DateRangeField";
import Selector from "pages/_components/fields/formik/Selector";
import CustomText from "pages/_components/CustomText/CustomText";
import TextField from "pages/_components/fields/TextField";
import PhoneInput from "pages/_components/fields/PhoneInput";
import Button from "pages/_components/Button";
import * as i18nUtils from "util/i18n";
import * as configUtils from "util/config";
import * as numberUtils from "util/number";
import { validateEmailWithRegex, validateCUILPF, validatePhone } from "util/form";
import { getLimitDate } from "util/date";
import CustomData from "pages/_components/CustomData/CustomData";
import { selectors as onboardingSelectors, actions as onboardingActions } from "reducers/onboarding";
import DateField from "pages/_components/fields/DateField";
import { connect, useDispatch } from "react-redux";
import { differenceInYears } from "date-fns";
import { useVerifyPersonRoles } from "../hooks/useVerifyPersonRoles";
import { usePhoneAreaCodesArg } from "../../../hooks/usePhoneAreaCodesArg";

const FORM_ID = "environment.step.beneficiaries";

const EnvironmentBeneficiarySlide = ({
    isMobile,
    countryList,
    pepJobList,
    pepRelationshipTypeList,
    slideIsVisible,
    closeSlide,
    stateList,
    localityList,
    onChangeState,
    fetchingStates,
    fetchingLocalities,
    onSubmit,
    shareholders,

    maritalStatusList,
    occupationList,
    foreignCountries,
    genders,
    nationalities,
}) => {
    const dispatch = useDispatch();
    const formRef = useRef(null);
    const [selectedCountryArg, setSelectedCountryArg] = useState(true); // Use null when there is more countries
    const [countryCode, setCountryCode] = useState("1"); // Use null when there is more countries
    const [countryName, setCountryName] = useState("");
    const [provinceName, setProvinceName] = useState("");
    const [localityName, setLocalityName] = useState("");
    const { argAreaCodes } = usePhoneAreaCodesArg();
    const [isSigner, setIsSigner] = useState(true);
    const [disableSignerCheckbox, setDisableSignerCheckbox] = useState(false);

    const [cuil, setCuil] = useState(null);

    const [defaultPhoneNumber] = useState("");
    const [lastSelectedProvince, setLastSelectedProvince] = useState("");

    const [capitalBeneficiaryMinValue, setCapitalBeneficiaryMinValue] = useState(10);

    const {
        verifiedUser,
        setVerifyUser,
        disabledForm,
        setDisabledForm,
        updatePhone,
        setUpdatePhone,
        verifyPersonRolesRequest,
        verifyPersonRoles,
        onChangeDocument,
        resetForm,
    } = useVerifyPersonRoles();

    useEffect(() => {
        const minValue = configUtils.getInteger("onboarding.finalBeneficiary.capitalHolding.min", 10);
        setCapitalBeneficiaryMinValue(minValue);
    }, []);

    const onChangeProvince = (selectedProvince) => {
        if (!!selectedProvince && lastSelectedProvince !== selectedProvince) {
            const stateId = stateList.filter((state) => state.value === selectedProvince)[0].codeId || "";
            formRef.current.setFieldValue("locality", "");
            setLastSelectedProvince(selectedProvince);
            dispatch(onboardingActions.listLocalityRequest("1", Number(stateId)));
        }
    };

    const cleanForm = () => {
        formRef.current.setFieldValue("email", "", false);
        formRef.current.setFieldValue("phoneNumber", "", false);
        formRef.current.setFieldValue("dni", "", false);
        formRef.current.setFieldValue("gender", "", false);
        formRef.current.setFieldValue("street", "", false);
        formRef.current.setFieldValue("streetNumber", "", false);
        formRef.current.setFieldValue("floor", "", false);
        formRef.current.setFieldValue("apartment", "", false);
        formRef.current.setFieldValue("postalCode", "", false);
        formRef.current.setFieldValue("bornDate", "", false);
        formRef.current.setFieldValue("firstName", "", false);
        formRef.current.setFieldValue("lastName", "", false);
        formRef.current.setFieldValue("province", "", false);
        formRef.current.setFieldValue("locality", "", false);
        formRef.current.setFieldValue("nationality", "", false);
        formRef.current.setFieldValue("maritalStatus", "", false);
        formRef.current.setFieldValue("occupation", "", false);
        formRef.current.setFieldValue("fatca", false, false);
        formRef.current.setFieldValue("fatcaNumber1", "", false);
        formRef.current.setFieldValue("fatcaNumber2", "", false);
        formRef.current.setFieldValue("fatcaNumber3", "", false);
        formRef.current.setFieldValue("crsOrOcde", false, false);
        formRef.current.setFieldValue("isLocalResident", "true", false);
        formRef.current.setFieldValue("localResidentNumber", "", false);
        formRef.current.setFieldValue("residentCountry", "", false);
        formRef.current.setFieldValue("foreignResidentNumber", "", false);
        formRef.current.setFieldValue("pep", false, false);
        formRef.current.setFieldValue("isPosition", "true", false);
        formRef.current.setFieldValue("positionType", "", false);
        formRef.current.setFieldValue("positionRange", [null, null], false);
        formRef.current.setFieldValue("relationType", "", false);
    };

    useEffect(() => {
        const {
            status: verifyPersonRolesRequestStatus,
            value: verifyPersonRolesRequestValue,
        } = verifyPersonRolesRequest;
        if (verifyPersonRolesRequestStatus === "SUCCESS") {
            if (formRef && !formRef.current) {
                return;
            }

            const { invitationData } = verifyPersonRolesRequestValue;

            if (isSigner && invitationData) {
                if (invitationData.mobilePhone && invitationData.email) {
                    formRef.current.setFieldValue("phoneNumber", invitationData?.mobilePhone, false);
                    formRef.current.setFieldTouched("phoneNumber", false, false);
                    setUpdatePhone(true);
                    formRef.current.setFieldValue("email", invitationData?.email);
                    formRef.current.setFieldTouched("email", false, false);
                    setDisableSignerCheckbox(invitationData?.isMultiroleSigner);

                    setDisabledForm(true);
                } else {
                    setDisabledForm(false);
                    resetForm(formRef);
                }
            } else if (invitationData) {
                if (invitationData.email) {
                    formRef.current.setFieldValue("email", invitationData.email, false);
                }
                if (invitationData.mobilePhone) {
                    formRef.current.setFieldValue("phoneNumber", invitationData.mobilePhone, false);
                }

                if (invitationData.isMultiroleSigner) {
                    formRef.current.setFieldValue("signer", "true", false);
                    setIsSigner(true);
                    setDisableSignerCheckbox(true);
                } else {
                    formRef.current.setFieldValue("country", "1", false);

                    if (invitationData.documentNumber) {
                        formRef.current.setFieldValue("dni", invitationData.documentNumber, false);
                    }
                    if (invitationData.gender) {
                        formRef.current.setFieldValue("gender", invitationData.gender, false);
                    }
                    if (invitationData.street) {
                        formRef.current.setFieldValue("street", invitationData.street, false);
                    }
                    if (invitationData.streetNumber) {
                        formRef.current.setFieldValue("streetNumber", invitationData.streetNumber, false);
                    }
                    if (invitationData.floor) {
                        formRef.current.setFieldValue("floor", invitationData.floor, false);
                    }
                    if (invitationData.apartment) {
                        formRef.current.setFieldValue("apartment", invitationData.apartment, false);
                    }
                    if (invitationData.postalCode) {
                        formRef.current.setFieldValue("postalCode", invitationData.postalCode, false);
                    }
                    if (invitationData.bornDate) {
                        formRef.current.setFieldValue("bornDate", invitationData.bornDate, false);
                    }
                    if (invitationData.firstName) {
                        formRef.current.setFieldValue("firstName", invitationData.firstName, false);
                    }
                    if (invitationData.lastName) {
                        formRef.current.setFieldValue("lastName", invitationData.lastName, false);
                    }
                    if (invitationData.province) {
                        formRef.current.setFieldValue("province", invitationData.province, false);
                        // Carga el listado de localidades para luego setearle el valor recibido
                        onChangeProvince(invitationData.province);
                    }
                    if (invitationData.locality) {
                        formRef.current.setFieldValue("locality", invitationData.locality, false);
                    }
                    if (invitationData.nationality) {
                        formRef.current.setFieldValue("nationality", invitationData.nationality, false);
                    }
                    if (invitationData.maritalStatus) {
                        formRef.current.setFieldValue("maritalStatus", invitationData.maritalStatus, false);
                    }
                    if (invitationData.occupation) {
                        formRef.current.setFieldValue("occupation", invitationData.occupation, false);
                    }
                    if (invitationData.SSN) {
                        formRef.current.setFieldValue("SSN", invitationData.SSN, false);
                    }
                    if (invitationData.hasPEP) {
                        formRef.current.setFieldValue("hasPEP", invitationData.hasPEP, false);
                    }
                    if (invitationData.pepPosition) {
                        formRef.current.setFieldValue("pepPosition", invitationData.pepPosition, false);
                    }
                    if (invitationData.pepRelation) {
                        formRef.current.setFieldValue("pepRelation", invitationData.pepRelation, false);
                    }
                    if (invitationData.dateToPep) {
                        formRef.current.setFieldValue("dateToPep", invitationData.dateToPep, false);
                    }
                    if (invitationData.dateFromPep) {
                        formRef.current.setFieldValue("dateFromPep", invitationData.dateFromPep, false);
                    }
                    if (invitationData.hasCRS) {
                        formRef.current.setFieldValue("hasCRS", invitationData.hasCRS, false);
                    }
                    if (invitationData.crsLocal) {
                        formRef.current.setFieldValue("crsLocal", invitationData.crsLocal, false);
                    }
                    if (invitationData.crsAbroad) {
                        formRef.current.setFieldValue("crsAbroad", invitationData.crsAbroad, false);
                    }
                    if (invitationData.crsResidentCountry) {
                        formRef.current.setFieldValue("crsResidentCountry", invitationData.crsResidentCountry, false);
                    }
                    setDisabledForm(!invitationData?.isEditable);
                    setDisableSignerCheckbox(invitationData?.isMultiroleSigner);
                }
            } else {
                setDisabledForm(false);
            }
        }
    }, [verifyPersonRolesRequest]);

    const closeSignerSlide = (allFormik) => {
        setVerifyUser(false);
        setDisabledForm(true);
        allFormik.resetForm();
        setSelectedCountryArg(true); // Use null when there is more countries
        setCountryCode("1"); // Use null when there is more countries
        closeSlide();
    };

    const selectCountry = (value) => {
        setCountryCode(value);

        // if (value === "1") {
        //     setSelectedCountryArg(true);
        // } else {
        //     setSelectedCountryArg(false);
        //     onChangeCountry(value);
        // }

        /**
         * Se habilita la selección de país
         * se setea selectedCountryArg para no modificar el formulario según el país
         * La persona debe tener dni argentino para completar el onboarding
         */

        setSelectedCountryArg(true);
        const country = countryList.find((c) => c.id === value);
        setCountryName(country.name);
    };

    const onChangeLocality = (value) => {
        const locality = localityList.find((l) => l.value === value);
        setLocalityName(locality.label);
    };

    const submitIsAllowed = (allFormik) => {
        const {
            documentNumber,
            country,
            signer,
            directRelationship,
            finalControl,
            capitalHoldingBeneficiary,
            phoneNumber,
            email,

            fatca,
            crsOrOcde,
            pep,
            fatcaNumber1,
            fatcaNumber2,
            fatcaNumber3,
            isLocalResident,
            localResidentNumber,
            residentCountry,
            foreignResidentNumber,
            isPosition,
            positionType,
            positionRange,
            relationRange,
            relationType,

            firstName,
            lastName,
            dni,
            nationality,
            gender,
            bornDate,
            maritalStatus,
            occupation,
            province,
            locality,
            street,
            streetNumber,
            postalCode,
        } = allFormik.values;

        if (!verifiedUser && !documentNumber) {
            return false;
        }

        if (verifiedUser) {
            if (signer === "true") {
                if (!phoneNumber.value || !validateEmailWithRegex(email)) {
                    return false;
                }
            } else {
                if (
                    !phoneNumber ||
                    !phoneNumber.value ||
                    !validateEmailWithRegex(email) ||
                    !firstName ||
                    !lastName ||
                    !dni ||
                    !nationality ||
                    !gender ||
                    !bornDate ||
                    !maritalStatus ||
                    !occupation ||
                    !country ||
                    !province ||
                    !locality ||
                    !street ||
                    !streetNumber ||
                    !postalCode
                ) {
                    return false;
                }

                if (!documentNumber || !country || !phoneNumber?.value || !validateEmailWithRegex(email)) {
                    return false;
                }
                if (selectedCountryArg === true) {
                    if (
                        !directRelationship ||
                        !finalControl ||
                        !capitalHoldingBeneficiary ||
                        capitalHoldingBeneficiary > 100
                    ) {
                        return false;
                    }
                }

                if (!!fatca && (!fatcaNumber1 || !fatcaNumber2 || !fatcaNumber3 || fatcaNumber3?.length < 4)) {
                    return false;
                }

                if (crsOrOcde) {
                    if (isLocalResident === "true" && localResidentNumber.length !== 11) {
                        return false;
                    }
                    if (isLocalResident === "false" && (!residentCountry || !foreignResidentNumber)) {
                        return false;
                    }
                }
                if (pep) {
                    if (isPosition === "true" && (!positionType || !positionRange[0] || !positionRange[1])) {
                        return false;
                    }
                    if (isPosition === "false" && (!relationType || !relationRange[0] || !relationRange[1])) {
                        return false;
                    }
                }
            }
        }
        return true;
    };

    const onChangeSignerRadioBtn = (checkBoxId, allFormik, newVal) => {
        if (newVal === "true") {
            setIsSigner(true);
        } else {
            setIsSigner(false);
        }
        // si se modifica el radio button de firmante, se debe resetear la busqueda de multirol y no onbordeante
        setVerifyUser(false);
        setDisabledForm(true);
        cleanForm();
    };

    const onChangeCheckbox = (checkBoxId, allFormik, newVal) => {
        if (!newVal && checkBoxId === "fatca") {
            allFormik.setFieldValue("fatcaNumber1", "", false);
            allFormik.setFieldValue("fatcaNumber2", "", false);
            allFormik.setFieldValue("fatcaNumber3", "", false);
            allFormik.setFieldTouched("fatcaNumber1", false, false);
            allFormik.setFieldTouched("fatcaNumber2", false, false);
            allFormik.setFieldTouched("fatcaNumber3", false, false);
        } else if (!newVal && checkBoxId === "crsOrOcde") {
            allFormik.setFieldValue("isLocalResident", "true", false);
            allFormik.setFieldValue("localResidentNumber", "", false);
            allFormik.setFieldValue("residentCountry", "", false);
            allFormik.setFieldValue("foreignResidentNumber", "", false);
            allFormik.setFieldTouched("isLocalResident", false, false);
            allFormik.setFieldTouched("localResidentNumber", false, false);
            allFormik.setFieldTouched("residentCountry", false, false);
            allFormik.setFieldTouched("foreignResidentNumber", false, false);
        } else if (!newVal && checkBoxId === "pep") {
            allFormik.setFieldValue("isPosition", "true", false);
            allFormik.setFieldValue("positionType", "", false);
            allFormik.setFieldValue("positionRange", [null, null], false);
            allFormik.setFieldValue("relationType", "", false);
            allFormik.setFieldTouched("isPosition", false, false);
            allFormik.setFieldTouched("positionType", false, false);
            allFormik.setFieldTouched("positionRange", false, false);
            allFormik.setFieldTouched("relationType", false, false);
        }
    };

    const onChangeRadioBtn = (checkBoxId, allFormik, newVal) => {
        if (checkBoxId === "isLocalResident" && newVal === "false") {
            allFormik.setFieldValue("localResidentNumber", "", false);
            allFormik.setFieldTouched("localResidentNumber", false, false);
        } else if (checkBoxId === "isLocalResident" && newVal === "true") {
            allFormik.setFieldValue("residentCountry", "", false);
            allFormik.setFieldValue("foreignResidentNumber", "", false);
            allFormik.setFieldTouched("residentCountry", false, false);
            allFormik.setFieldTouched("foreignResidentNumber", false, false);
        } else if (checkBoxId === "isPosition" && newVal === "false") {
            allFormik.setFieldValue("positionType", "", false);
            allFormik.setFieldValue("positionRange", [null, null], false);
            allFormik.setFieldTouched("positionType", false, false);
            allFormik.setFieldTouched("positionRange", false, false);
        } else if (checkBoxId === "isPosition" && newVal === "true") {
            allFormik.setFieldValue("relationType", "", false);
            allFormik.setFieldTouched("relationType", false, false);
        }
    };

    const requiredString = i18nUtils.get("forms.inputFile.massivePayments.invalid.required");
    const numberTest = (maxLength) =>
        Yup.string()
            .required(requiredString)
            .test(
                "format-number",
                i18nUtils.get("forms.inputFile.massivePayments.invalid.field"),
                (value) =>
                    !!value &&
                    !Number.isNaN(Number(value)) &&
                    !value?.includes(" ") &&
                    (maxLength ? value?.length === maxLength : true),
            );

    const formikProps = {
        validateOnChange: true,
        validateOnBlur: false,
        initialValues: {
            country: "1", // Put as empty string when there is more countries
            countryName: "Argentina", // Put as empty string when there is more countries
            documentNumber: "",
            documentType: "",
            directRelationship: "",
            finalControl: "",
            capitalHoldingBeneficiary: "",
            name: "",
            surname: "",
            phoneNumber: { countryCode: "", areaCode: "", value: "" },
            email: "",
            signer: "true",
            provinceName: "",
            localityName: "",

            firstName: "",
            lastName: "",
            dni: "",
            nationality: "",
            gender: "",
            bornDate: "",
            maritalStatus: "",
            occupation: "",
            province: "",
            locality: "",
            street: "",
            streetNumber: "",
            floor: "",
            apartment: "",
            postalCode: "",

            fatca: false,
            fatcaNumber1: "",
            fatcaNumber2: "",
            fatcaNumber3: "",

            crsOrOcde: false,
            isLocalResident: "true",
            localResidentNumber: "",
            residentCountry: "",
            foreignResidentNumber: "",

            pep: false,
            isPosition: "true",
            positionType: "",
            positionRange: [null, null],
            relationRange: [null, null],
            relationType: "",
        },
        // eslint-disable-next-line no-confusing-arrow
        validationSchema: () =>
            // eslint-disable-next-line no-nested-ternary
            !verifiedUser
                ? Yup.object().shape({
                      country: Yup.string().required(requiredString),
                      documentNumber: Yup.string()
                          .required(i18nUtils.get("global.error.required"))
                          .test(
                              "cuil",
                              i18nUtils.get(
                                  "onboarding.step.personalData.cuil.error.invalidFormat",
                                  "Formato de CUIL inválido",
                              ),
                              (value) => validateCUILPF(value),
                          ),
                  })
                : isSigner
                ? Yup.object().shape({
                      country: Yup.string().required(requiredString),
                      documentNumber: Yup.string()
                          .required(i18nUtils.get("global.error.required"))
                          .test(
                              "cuil",
                              i18nUtils.get(
                                  "onboarding.step.personalData.cuil.error.invalidFormat",
                                  "Formato de CUIL inválido",
                              ),
                              (value) => validateCUILPF(value),
                          ),
                      directRelationship: Yup.bool().required(requiredString),
                      finalControl: Yup.bool().required(requiredString),
                      signer: Yup.string().required(requiredString),
                      capitalHoldingBeneficiary: Yup.string()
                          .required(requiredString)
                          .test(
                              "is-valid-capital-holding",
                              i18nUtils.get(
                                  "onboarding.step.beneficiaries.error.number.over",
                                  "El porcentaje de tenencia de capital/control no puede ser mayor al 100%",
                              ),
                              (value) => {
                                  // Convert the value to a number and then check if it's less than or equal to 100
                                  const numValue = numberUtils.toNumber(value, ".", 2);
                                  return numValue <= 100;
                              },
                          ),
                      phoneNumber: validatePhone(argAreaCodes),
                      email: Yup.string()
                          .required(i18nUtils.get("onboarding.step.verifyEmail.email.error.empty"))
                          .test(
                              "format",
                              i18nUtils.get("onboarding.step.verifyEmail.email.error.invalidFormat"),
                              (value) => !!value && validateEmailWithRegex(value),
                          ),
                  })
                : Yup.object().shape({
                      country: Yup.string().required(requiredString),
                      documentNumber: Yup.string()
                          .required(i18nUtils.get("global.error.required"))
                          .test(
                              "cuil",
                              i18nUtils.get(
                                  "onboarding.step.personalData.cuil.error.invalidFormat",
                                  "Formato de CUIL inválido",
                              ),
                              (value) => validateCUILPF(value),
                          ),
                      directRelationship: selectedCountryArg
                          ? Yup.bool().required(requiredString)
                          : Yup.bool().notRequired(),
                      finalControl: selectedCountryArg ? Yup.bool().required(requiredString) : Yup.bool().notRequired(),
                      capitalHoldingBeneficiary: selectedCountryArg
                          ? Yup.string()
                                .required(requiredString)
                                .test(
                                    "is-valid-capital-holding",
                                    i18nUtils.get(
                                        "onboarding.step.beneficiaries.error.number.over",
                                        "El porcentaje de tenencia de capital/control no puede ser mayor al 100%",
                                    ),
                                    (value) => {
                                        // Convert the value to a number and then check if it's less than or equal to 100
                                        const numValue = numberUtils.toNumber(value, ".", 2);
                                        return numValue <= 100;
                                    },
                                )
                          : Yup.string().notRequired(),
                      phoneNumber: validatePhone(argAreaCodes),
                      email: Yup.string()
                          .required(i18nUtils.get("onboarding.step.verifyEmail.email.error.empty"))
                          .test(
                              "format",
                              i18nUtils.get("onboarding.step.verifyEmail.email.error.invalidFormat"),
                              (value) => validateEmailWithRegex(value),
                          ),
                      firstName: Yup.string().required(requiredString),
                      lastName: Yup.string().required(requiredString),
                      dni: Yup.string()
                          .required(requiredString)
                          .test(
                              "format",
                              i18nUtils.get(
                                  "onboarding.dni.invalid",
                                  "El DNI no se corresponde con el cuil ingresado.",
                              ),
                              (value) => !!value && value.length === 8 && cuil.includes(value),
                          ),
                      nationality: Yup.string().required(requiredString),
                      gender: Yup.string().required(requiredString),
                      bornDate: Yup.date()
                          .required(requiredString)
                          .test("age", i18nUtils.get("onboarding.pj.age.error.message"), (value) => {
                              const minAge = configUtils.getInteger("onboarding.document.age.valid", 18);
                              return differenceInYears(new Date(), new Date(value)) >= minAge;
                          }),
                      maritalStatus: Yup.string().required(requiredString),
                      occupation: Yup.string().required(requiredString),
                      province: Yup.string().required(requiredString),
                      locality: Yup.string().required(requiredString),
                      street: Yup.string().required(requiredString),
                      streetNumber: Yup.string()
                          .required(requiredString)
                          .test(
                              "format-streetNumber",
                              i18nUtils.get("forms.inputFile.massivePayments.invalid.field"),
                              (value) => !Number.isNaN(Number(value)),
                          ),
                      floor: Yup.string(),
                      apartment: Yup.string(),
                      postalCode: Yup.string().required(requiredString),

                      fatca: Yup.bool().required(requiredString),
                      fatcaNumber1: Yup.string().when("fatca", { is: true, then: numberTest(3) }),
                      fatcaNumber2: Yup.string().when("fatca", { is: true, then: numberTest(2) }),
                      fatcaNumber3: Yup.string().when("fatca", { is: true, then: numberTest(4) }),

                      crsOrOcde: Yup.bool().required(requiredString),
                      isLocalResident: Yup.string().when("crsOrOcde", {
                          is: true,
                          then: Yup.string().required(requiredString),
                      }),
                      localResidentNumber: Yup.string().when(
                          ["crsOrOcde", "isLocalResident"],
                          (crsOrOcde, isLocalResident, schema) => {
                              if (!!crsOrOcde && isLocalResident === "true") {
                                  return numberTest(11);
                              }
                              return schema;
                          },
                      ),
                      residentCountry: Yup.string().when(
                          ["crsOrOcde", "isLocalResident"],
                          (crsOrOcde, isLocalResident, schema) => {
                              if (!!crsOrOcde && isLocalResident === "false") {
                                  return schema.required(requiredString);
                              }
                              return schema;
                          },
                      ),
                      foreignResidentNumber: Yup.string().when(
                          ["crsOrOcde", "isLocalResident"],
                          (crsOrOcde, isLocalResident, schema) => {
                              if (!!crsOrOcde && isLocalResident === "false") {
                                  return Yup.string().required(requiredString);
                              }
                              return schema;
                          },
                      ),
                      pep: Yup.bool().required(requiredString),
                      isPosition: Yup.string().when("pep", {
                          is: true,
                          then: Yup.string().required(requiredString),
                      }),
                      positionType: Yup.string().when(["pep", "isPosition"], (pep, isPosition, schema) => {
                          if (!!pep && isPosition === "true") {
                              return schema.required(requiredString);
                          }
                          return schema;
                      }),
                      positionRange: Yup.array()
                          .of(Yup.date().nullable())
                          .when(["pep", "isPosition"], (pep, isPosition, schema) => {
                              if (!!pep && isPosition === "true") {
                                  return schema.required(requiredString);
                              }
                              return schema;
                          }),
                      relationRange: Yup.array()
                          .of(Yup.date().nullable())
                          .when(["pep", "isPosition"], (pep, isPosition, schema) => {
                              if (!!pep && isPosition === "false") {
                                  return schema.required(requiredString);
                              }
                              return schema;
                          }),
                      relationType: Yup.string().when(["pep", "isPosition"], (pep, isPosition, schema) => {
                          if (!!pep && isPosition === "false") {
                              return schema.required(requiredString);
                          }
                          return schema;
                      }),
                  }),
        onSubmit: (values, formikBag) => {
            if (values) {
                const found =
                    shareholders.length > 0
                        ? shareholders?.find((element) => element.documentNumber === values.documentNumber)
                        : false;

                if (found) {
                    formikBag.setErrors({
                        documentNumber: i18nUtils.get("", "CUIL ya se encuentra agregado"),
                    });
                    formikBag.setSubmitting(false);
                    return;
                }

                if (verifiedUser) {
                    closeSignerSlide(formikBag);
                    onSubmit({
                        documentNumber: values.documentNumber,
                        documentType: "CUIL",
                        directRelationship: values?.directRelationship,
                        finalControl: values?.finalControl,

                        countryName,
                        provinceName,
                        localityName,

                        capitalHoldingBeneficiary: values.capitalHoldingBeneficiary,
                        signer: isSigner,
                        phoneNumber: values.phoneNumber,
                        email: values?.email,

                        firstName: values?.firstName,
                        lastName: values?.lastName,
                        dni: values?.dni,
                        nationality: values?.nationality,
                        gender: values?.gender,
                        bornDate: values?.bornDate,
                        maritalStatus: values?.maritalStatus,
                        occupation: values?.occupation,
                        country: values?.country,
                        province: values?.province,
                        locality: values?.locality,
                        street: values?.street,
                        streetNumber: values?.streetNumber,
                        floor: values?.floor,
                        apartment: values?.apartment,
                        postalCode: values?.postalCode,

                        fatca: values?.fatca,
                        fatcaNumber1: values?.fatcaNumber1,
                        fatcaNumber2: values?.fatcaNumber2,
                        fatcaNumber3: values?.fatcaNumber3,
                        crsOrOcde: values?.crsOrOcde,
                        localResident: values?.isLocalResident === "true",
                        localResidentNumber: values?.localResidentNumber,
                        residentCountry: values?.residentCountry,
                        foreignResidentNumber: values?.foreignResidentNumber,
                        pep: values?.pep,
                        position: values?.isPosition === "true",
                        positionType: values?.positionType,
                        dateFromPep:
                            values?.isPosition === "true" ? values?.positionRange[0] : values?.relationRange[0],
                        dateToPep: values?.isPosition === "true" ? values?.positionRange[1] : values?.relationRange[1],
                        relationType: values?.relationType,
                        isMultiroleSigner: disableSignerCheckbox,
                        isEditable: disabledForm,
                    });
                } else {
                    setCuil(values?.documentNumber);
                    verifyPersonRoles(values?.documentNumber, "BENEFICIARIO_FINAL", isSigner);
                }
            }
        },
    };

    return (
        <Formik {...formikProps} ref={formRef}>
            {(allFormik) => (
                <Drawer
                    className="signer-slider"
                    openSidebar={slideIsVisible}
                    toggleSidebar={() => closeSignerSlide(allFormik)}>
                    <div className="container">
                        <h2 className="title-content">
                            <CustomText text={i18nUtils.get("", "Beneficiario final")} />
                        </h2>
                        <Form>
                            <div>
                                <Field
                                    component={Selector}
                                    options={
                                        countryList &&
                                        countryList.map((c) => ({
                                            value: c.id,
                                            label: c.name,
                                        }))
                                    }
                                    idForm={FORM_ID}
                                    classNameForViewMode="data-desc"
                                    maxLength={50}
                                    name="country"
                                    searchable
                                    onChange={(value) => selectCountry(value)}
                                    label={`${i18nUtils.get("environment.step.beneficiaries.country.label", "País")}`}
                                    placeholder={`${i18nUtils.get(
                                        "environment.step.beneficiaries.country.placeholder",
                                        "Elegí una opción",
                                    )}`}
                                />

                                {selectedCountryArg && (
                                    <div>
                                        <I18n id="" defaultValue={i18nUtils.get("", "CUIL")} />
                                        <Field
                                            autoComplete="off"
                                            component={TextField}
                                            idForm={FORM_ID}
                                            hideLabel
                                            name="documentNumber"
                                            spaceEnabled={false}
                                            hidePlaceholder
                                            handleOnChange={(ev) => {
                                                onChangeDocument(ev);
                                                setDisableSignerCheckbox(false);
                                                cleanForm();
                                            }}
                                        />
                                    </div>
                                )}

                                {selectedCountryArg && (
                                    <div>
                                        <I18n id="" defaultValue={i18nUtils.get("", "Tipo de relación")} />

                                        <Field
                                            component={Selector}
                                            renderAs="radio"
                                            idForm={FORM_ID}
                                            name="directRelationship"
                                            options={[
                                                {
                                                    id: "true",
                                                    label: i18nUtils.get("", "Directa"),
                                                },
                                                {
                                                    id: "false",
                                                    label: i18nUtils.get("", "Indirecta"),
                                                },
                                            ]}
                                        />
                                    </div>
                                )}
                                {selectedCountryArg && (
                                    <div>
                                        <I18n id="" defaultValue={i18nUtils.get("", "¿Ejerce control final?")} />

                                        <Field
                                            component={Selector}
                                            renderAs="radio"
                                            idForm={FORM_ID}
                                            name="finalControl"
                                            options={[
                                                {
                                                    id: "true",
                                                    label: i18nUtils.get("global.yes", "Sí"),
                                                },
                                                {
                                                    id: "false",
                                                    label: i18nUtils.get("global.no", "No"),
                                                },
                                            ]}
                                        />
                                    </div>
                                )}
                                {selectedCountryArg && (
                                    <div>
                                        <I18n
                                            id=""
                                            defaultValue={i18nUtils.get("", "Tenencia de capital/control (%)")}
                                        />
                                        <Field
                                            autoComplete="off"
                                            component={TextField}
                                            idForm={FORM_ID}
                                            hideLabel
                                            name="capitalHoldingBeneficiary"
                                            spaceEnabled={false}
                                            hidePlaceholder
                                            onlyNumbers
                                            maxLength={3}
                                        />
                                    </div>
                                )}
                                <I18n
                                    id=""
                                    defaultValue={i18nUtils.get("", "¿Formarás parte del esquema de firmas?")}
                                />
                                <Field
                                    component={Selector}
                                    renderAs="radio"
                                    inLineControl
                                    idForm={FORM_ID}
                                    name="signer"
                                    options={[
                                        {
                                            id: "true",
                                            label: i18nUtils.get("global.yes", "Si"),
                                        },
                                        {
                                            id: "false",
                                            label: i18nUtils.get("global.no", "No"),
                                        },
                                    ]}
                                    onChange={(v) => onChangeSignerRadioBtn("signer", allFormik, v)}
                                />
                                {!isSigner && (
                                    <div className="container--alert" role="alert">
                                        <div className="alert alert-info">
                                            <CustomData
                                                icon="information_fill.svg"
                                                titleText={i18nUtils.get(
                                                    "onboarding.step.legalRepresentative.legal",
                                                    "Al seleccionar “No”, tené en cuenta que no tendrá credenciales para realizar operaciones ni consultas.",
                                                )}
                                                keepSpaceIfNoValue={false}
                                            />
                                        </div>
                                    </div>
                                )}
                                {verifiedUser && isSigner && (
                                    <>
                                        <Field
                                            component={PhoneInput}
                                            idForm={FORM_ID}
                                            name="phoneNumber"
                                            country="AR"
                                            mobilePhone="54"
                                            enableSearch={false}
                                            preferredCountries={["ar"]}
                                            labelTextDefault={i18nUtils.get("", "Celular")}
                                            hintLabel={i18nUtils.get(
                                                "",
                                                "Debes incluir el código de área y luego el número.",
                                            )}
                                            disabled={disabledForm}
                                            updatePhone={updatePhone}
                                            phoneAlreadyUpdated={() => setUpdatePhone(false)}
                                        />
                                        <Field
                                            autoComplete="off"
                                            component={TextField}
                                            idForm={FORM_ID}
                                            name="email"
                                            labelTextDefault={i18nUtils.get("", "Email")}
                                            spaceEnabled={false}
                                            hidePlaceholder
                                            disabled={disabledForm}
                                        />
                                    </>
                                )}

                                {verifiedUser && !isSigner && (
                                    <>
                                        <div className="grid-table-row grid-cols-12 col-span-10">
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <I18n id="" defaultValue="Datos personales" component="b" />
                                            </div>
                                            <br />
                                            <div className="grid-table-data col-start-2 col-span-12">
                                                <I18n
                                                    id=""
                                                    defaultValue="Verificá que los datos sean correctos, en caso contrario podés corregirlos."
                                                />
                                            </div>
                                        </div>
                                        <br />
                                        <div className="grid-table-row grid-cols-12 col-span-10">
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <Field
                                                    component={PhoneInput}
                                                    idForm={FORM_ID}
                                                    name="phoneNumber"
                                                    country="AR"
                                                    mobilePhone={defaultPhoneNumber || "54"}
                                                    enableSearch={false}
                                                    preferredCountries={["ar"]}
                                                    labelTextDefault={i18nUtils.get("", "Celular")}
                                                    hintLabel={i18nUtils.get(
                                                        "",
                                                        "Debes incluir el código de área y luego el número.",
                                                    )}
                                                    updatePhone={updatePhone}
                                                    phoneAlreadyUpdated={() => setUpdatePhone(false)}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-7 col-span-5">
                                                <Field
                                                    autoComplete="off"
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    maxLength={50}
                                                    name="email"
                                                    labelTextDefault={i18nUtils.get("", "Email")}
                                                    spaceEnabled={false}
                                                    hidePlaceholder
                                                />
                                            </div>
                                        </div>
                                        <div className="grid-table-row grid-cols-12 col-span-10">
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <Field
                                                    autoComplete="off"
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    maxLength={50}
                                                    name="firstName"
                                                    labelTextDefault={i18nUtils.get("", "Nombre")}
                                                    hidePlaceholder
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-7 col-span-5">
                                                <Field
                                                    autoComplete="off"
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    maxLength={50}
                                                    name="lastName"
                                                    labelTextDefault={i18nUtils.get("", "Apellido")}
                                                    hidePlaceholder
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <Field
                                                    autoComplete="off"
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    maxLength={50}
                                                    name="dni"
                                                    labelTextDefault={i18nUtils.get("", "Documento de identidad")}
                                                    spaceEnabled={false}
                                                    hidePlaceholder
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-7 col-span-5">
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="nationality"
                                                    options={
                                                        nationalities.map((value) => ({
                                                            value,
                                                            label: value,
                                                        })) || []
                                                    }
                                                    label={`${i18nUtils.get("", "Nacionalidad")}`}
                                                    placeholder={`${i18nUtils.get("", "Elegir una opción")}`}
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="gender"
                                                    options={
                                                        genders.map((value) => ({
                                                            value,
                                                            label: i18nUtils.get(
                                                                `onboarding.selectors.pf.genderCode.${value}`,
                                                            ),
                                                        })) || []
                                                    }
                                                    label={`${i18nUtils.get("", "Género/Sexo")}`}
                                                    placeholder={`${i18nUtils.get("", "Elegir una opción")}`}
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-7 col-span-5">
                                                <Field
                                                    component={DateField}
                                                    idForm={FORM_ID}
                                                    name="bornDate"
                                                    labelKey="onboarding.step4.dateOfBirth.label"
                                                    labelText="Fecha de nacimiento"
                                                    minDate={null}
                                                    maxDate={new Date()}
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="maritalStatus"
                                                    options={maritalStatusList || []}
                                                    label={`${i18nUtils.get("", "Estado civil")}`}
                                                    placeholder={`${i18nUtils.get("", "Elegir una opción")}`}
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-7 col-span-5">
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="occupation"
                                                    options={occupationList || []}
                                                    label={`${i18nUtils.get("", "Actividad / ocupación")}`}
                                                    placeholder={`${i18nUtils.get("", "Elegir una opción")}`}
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                        </div>
                                        <br />
                                        <div className="grid-table-row grid-cols-12 col-span-10">
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <I18n id="" defaultValue="Domicilio" component="b" />
                                            </div>
                                            <br />
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="country"
                                                    options={[{ value: "1", label: "Argentina" }]}
                                                    label={`${i18nUtils.get(
                                                        "onboarding.personalData.country.label",
                                                        "País",
                                                    )}`}
                                                    placeholder="Argentina"
                                                    disabled
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-7 col-span-5">
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="province"
                                                    onChange={onChangeProvince}
                                                    options={stateList}
                                                    label={`${i18nUtils.get(
                                                        "onboarding.personalData.province.label",
                                                        "Provincia",
                                                    )}`}
                                                    placeholder={`${i18nUtils.get(
                                                        "onboarding.personalData.selectorPlaceholder",
                                                        "Elegir una opción",
                                                    )}`}
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="locality"
                                                    options={localityList}
                                                    label={`${i18nUtils.get(
                                                        "onboarding.personalData.locality.label",
                                                        "Localidad",
                                                    )}`}
                                                    placeholder={`${i18nUtils.get(
                                                        "onboarding.personalData.selectorPlaceholder",
                                                        "Elegir una opción",
                                                    )}`}
                                                    disabled={
                                                        (allFormik?.values?.province !== "" && fetchingLocalities) ||
                                                        disabledForm
                                                    }
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-7 col-span-5">
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="street"
                                                    labelTextDefault={`${i18nUtils.get(
                                                        "onboarding.personalData.street.label",
                                                        "Calle",
                                                    )}`}
                                                    hidePlaceholder
                                                    hasRegex="^[A-Za-z0-9\-_\. ]*$"
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="streetNumber"
                                                    labelTextDefault={`${i18nUtils.get(
                                                        "onboarding.personalData.streetNumber.label",
                                                        "Número",
                                                    )}`}
                                                    hidePlaceholder
                                                    onlyNumbers
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-7 col-span-5">
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="floor"
                                                    labelTextDefault={`${i18nUtils.get(
                                                        "onboarding.personalData.floor.label",
                                                        "Piso",
                                                    )}`}
                                                    hidePlaceholder
                                                    onlyNumbers
                                                    maxLength={2}
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="apartment"
                                                    labelTextDefault={`${i18nUtils.get(
                                                        "onboarding.personalData.apartment.label",
                                                        "Departamento",
                                                    )}`}
                                                    hidePlaceholder
                                                    hasRegex="^[A-Za-z0-9\-_\. ]*$"
                                                    maxLength={3}
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                            <div className="grid-table-data col-start-7 col-span-5">
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="postalCode"
                                                    labelTextDefault={`${i18nUtils.get(
                                                        "onboarding.personalData.postalCode.label",
                                                        "Código postal",
                                                    )}`}
                                                    hidePlaceholder
                                                    onlyNumbers
                                                    disabled={disabledForm}
                                                />
                                            </div>
                                        </div>
                                        <div className="grid-table-row grid-cols-12 col-span-10">
                                            <div className="grid-table-data col-start-2 col-span-10 subtitle">
                                                <I18n id="" defaultValue="Datos adicionales" component="h2" />
                                            </div>
                                            <div className="grid-table-data col-start-2 col-span-10">
                                                <ul className="list">
                                                    <li>
                                                        <Field
                                                            component={Checkbox}
                                                            idForm={FORM_ID}
                                                            name="fatca"
                                                            labelText="onboarding.regulatoryData.fatca.label"
                                                            labelTextProps={{
                                                                defaultValue:
                                                                    "¿Soy residente fiscal en el exterior (FATCA)?",
                                                            }}
                                                            onChange={(v) => onChangeCheckbox("fatca", allFormik, v)}
                                                            disabled={disabledForm}
                                                        />
                                                        {!!allFormik?.values?.fatca && (
                                                            <>
                                                                <p>
                                                                    <I18n
                                                                        id="onboarding.regulatoryData.fatcaNumber.label"
                                                                        defaultValue="Número de seguro social (SSN)"
                                                                    />
                                                                </p>
                                                                <div className="grid grid-cols-3 grid-rows-1 gap-x-3 items-start">
                                                                    <Field
                                                                        component={TextField}
                                                                        idForm={FORM_ID}
                                                                        name="fatcaNumber1"
                                                                        hideLabel
                                                                        maxLength="3"
                                                                        placeholder={`${i18nUtils.get(
                                                                            "onboarding.regulatoryData.fatcaNumber1.placeholder",
                                                                            "XXX",
                                                                        )}`}
                                                                        hintLabel={`${i18nUtils.get(
                                                                            "onboarding.regulatoryData.fatcaNumber1.hintLabel",
                                                                            "3 dígitos.",
                                                                        )}`}
                                                                        autoFocus={!isMobile}
                                                                        disabled={disabledForm}
                                                                    />
                                                                    <Field
                                                                        component={TextField}
                                                                        idForm={FORM_ID}
                                                                        name="fatcaNumber2"
                                                                        hideLabel
                                                                        maxLength="2"
                                                                        placeholder={`${i18nUtils.get(
                                                                            "onboarding.regulatoryData.fatcaNumber2.placeholder",
                                                                            "XX",
                                                                        )}`}
                                                                        hintLabel={`${i18nUtils.get(
                                                                            "onboarding.regulatoryData.fatcaNumber2.hintLabel",
                                                                            "2 dígitos.",
                                                                        )}`}
                                                                        disabled={disabledForm}
                                                                    />
                                                                    <Field
                                                                        component={TextField}
                                                                        idForm={FORM_ID}
                                                                        name="fatcaNumber3"
                                                                        hideLabel
                                                                        maxLength="4"
                                                                        placeholder={`${i18nUtils.get(
                                                                            "onboarding.regulatoryData.fatcaNumber3.placeholder",
                                                                            "XXXX",
                                                                        )}`}
                                                                        hintLabel={`${i18nUtils.get(
                                                                            "onboarding.regulatoryData.fatcaNumber3.hintLabel",
                                                                            "4 dígitos.",
                                                                        )}`}
                                                                        disabled={disabledForm}
                                                                    />
                                                                </div>
                                                            </>
                                                        )}
                                                    </li>
                                                    <li>
                                                        <Field
                                                            component={Checkbox}
                                                            idForm={FORM_ID}
                                                            name="crsOrOcde"
                                                            labelText="onboarding.regulatoryData.crsOrOcde.label"
                                                            labelTextProps={{
                                                                defaultValue:
                                                                    "¿Soy residente fiscal en el exterior (CRS/OCE)?",
                                                            }}
                                                            onChange={(v) =>
                                                                onChangeCheckbox("crsOrOcde", allFormik, v)
                                                            }
                                                            disabled={disabledForm}
                                                        />
                                                        {!!allFormik?.values?.crsOrOcde && (
                                                            <>
                                                                <Field
                                                                    component={Selector}
                                                                    renderAs="radio"
                                                                    idForm={FORM_ID}
                                                                    name="isLocalResident"
                                                                    onChange={(v) =>
                                                                        onChangeRadioBtn(
                                                                            "isLocalResident",
                                                                            allFormik,
                                                                            v,
                                                                        )
                                                                    }
                                                                    options={[
                                                                        {
                                                                            id: "true",
                                                                            label: i18nUtils.get(
                                                                                "onboarding.regulatoryData.isLocalResident.true.label",
                                                                                "Residente Local",
                                                                            ),
                                                                        },
                                                                        {
                                                                            id: "false",
                                                                            label: i18nUtils.get(
                                                                                "onboarding.regulatoryData.isLocalResident.false.label",
                                                                                "Residente Exterior",
                                                                            ),
                                                                        },
                                                                    ]}
                                                                    disabled={disabledForm}
                                                                />
                                                                {allFormik?.values?.isLocalResident === "true" ? (
                                                                    <Field
                                                                        component={TextField}
                                                                        idForm={FORM_ID}
                                                                        name="localResidentNumber"
                                                                        labelTextDefault={`${i18nUtils.get(
                                                                            "onboarding.regulatoryData.localResidentNumber.label",
                                                                            "CUIL / CUIT",
                                                                        )}`}
                                                                        placeholder={`${i18nUtils.get(
                                                                            "onboarding.regulatoryData.localResidentNumber.placeholder",
                                                                            "ej: 30403333334",
                                                                        )}`}
                                                                        hintLabel={`${i18nUtils.get(
                                                                            "onboarding.regulatoryData.localResidentNumber.hintLabel",
                                                                            "Debe contener 11 caracteres.",
                                                                        )}`}
                                                                        autoFocus={!isMobile}
                                                                        disabled={disabledForm}
                                                                    />
                                                                ) : (
                                                                    <>
                                                                        <Field
                                                                            component={Selector}
                                                                            idForm={FORM_ID}
                                                                            name="residentCountry"
                                                                            options={foreignCountries || []}
                                                                            label={`${i18nUtils.get(
                                                                                "onboarding.regulatoryData.residentCountry.label",
                                                                                "País",
                                                                            )}`}
                                                                            placeholder={`${i18nUtils.get(
                                                                                "onboarding.personalData.selectorPlaceholder",
                                                                                "Elegir una opción",
                                                                            )}`}
                                                                            disabled={disabledForm}
                                                                        />
                                                                        <Field
                                                                            component={TextField}
                                                                            idForm={FORM_ID}
                                                                            name="foreignResidentNumber"
                                                                            labelTextDefault={`${i18nUtils.get(
                                                                                "onboarding.regulatoryData.foreignResidentNumber.label",
                                                                                "NIF",
                                                                            )}`}
                                                                            placeholder={`${i18nUtils.get(
                                                                                "onboarding.regulatoryData.fatcaNumber1.placeholder",
                                                                                "ej: 403333333",
                                                                            )}`}
                                                                            disabled={disabledForm}
                                                                        />
                                                                    </>
                                                                )}
                                                            </>
                                                        )}
                                                    </li>
                                                    <li>
                                                        <Field
                                                            component={Checkbox}
                                                            idForm={FORM_ID}
                                                            name="pep"
                                                            labelText="onboarding.regulatoryData.pep.label"
                                                            labelTextProps={{
                                                                defaultValue:
                                                                    "¿Soy Persona Expuesta Políticamente (PEP)?",
                                                            }}
                                                            onChange={(v) => onChangeCheckbox("pep", allFormik, v)}
                                                            disabled={disabledForm}
                                                        />
                                                        {!!allFormik?.values?.pep && (
                                                            <>
                                                                <Field
                                                                    component={Selector}
                                                                    renderAs="radio"
                                                                    idForm={FORM_ID}
                                                                    name="isPosition"
                                                                    onChange={(v) =>
                                                                        onChangeRadioBtn("isPosition", allFormik, v)
                                                                    }
                                                                    options={[
                                                                        {
                                                                            id: "true",
                                                                            label: i18nUtils.get(
                                                                                "onboarding.regulatoryData.isPosition.true.label",
                                                                                "Cargo",
                                                                            ),
                                                                        },
                                                                        {
                                                                            id: "false",
                                                                            label: i18nUtils.get(
                                                                                "onboarding.regulatoryData.isPosition.false.label",
                                                                                "Relación",
                                                                            ),
                                                                        },
                                                                    ]}
                                                                    disabled={disabledForm}
                                                                />
                                                                {allFormik?.values?.isPosition === "true" ? (
                                                                    <>
                                                                        <Field
                                                                            component={Selector}
                                                                            idForm={FORM_ID}
                                                                            name="positionType"
                                                                            options={pepJobList || []}
                                                                            label={`${i18nUtils.get(
                                                                                "onboarding.regulatoryData.positionType.label",
                                                                                "Tipo de Cargo",
                                                                            )}`}
                                                                            placeholder={`${i18nUtils.get(
                                                                                "onboarding.personalData.selectorPlaceholder",
                                                                                "Elegir una opción",
                                                                            )}`}
                                                                            disabled={disabledForm}
                                                                        />
                                                                        <Field
                                                                            component={DateRangeField}
                                                                            idForm={FORM_ID}
                                                                            defaultLabel="Fecha fin de cargo PEP"
                                                                            minDate={getLimitDate(-3650)}
                                                                            maxDate={getLimitDate(3650)}
                                                                            name="positionRange"
                                                                            dateFormat="dd/MM/yyyy"
                                                                            selectsRange
                                                                            showMonthYearDropdown
                                                                            scrollableMonthYearDropdown
                                                                            popperPlacement="bottom"
                                                                            showGlobalPlaceholder
                                                                            disabled={disabledForm}
                                                                        />
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Field
                                                                            component={Selector}
                                                                            idForm={FORM_ID}
                                                                            name="relationType"
                                                                            options={pepRelationshipTypeList || []}
                                                                            label={`${i18nUtils.get(
                                                                                "onboarding.regulatoryData.relationType.label",
                                                                                "Tipo de Relación",
                                                                            )}`}
                                                                            placeholder={`${i18nUtils.get(
                                                                                "onboarding.personalData.selectorPlaceholder",
                                                                                "Elegir una opción",
                                                                            )}`}
                                                                            disabled={disabledForm}
                                                                        />
                                                                    </>
                                                                )}
                                                            </>
                                                        )}
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {selectedCountryArg !== null && !selectedCountryArg && (
                                    <div>
                                        <I18n id="" defaultValue={i18nUtils.get("", "Documento")} />
                                        <Field
                                            autoComplete="off"
                                            component={TextField}
                                            idForm={FORM_ID}
                                            hideLabel
                                            name="documentNumber"
                                            spaceEnabled={false}
                                            hidePlaceholder
                                        />
                                    </div>
                                )}

                                {selectedCountryArg !== null && !selectedCountryArg && (
                                    <div>
                                        <I18n id="" defaultValue={i18nUtils.get("", "Domicilio")} />
                                        <Field
                                            component={Selector}
                                            idForm={FORM_ID}
                                            name="province"
                                            onChange={onChangeProvince}
                                            disabled={fetchingStates}
                                            options={
                                                stateList &&
                                                stateList.map((c) => ({
                                                    value: c.code,
                                                    label: c.description,
                                                }))
                                            }
                                            label={`${i18nUtils.get(
                                                "environment.step.beneficiaries.province.label",
                                                "Provincia",
                                            )}`}
                                            placeholder={`${i18nUtils.get(
                                                "environment.step.beneficiaries.province.placeholder",
                                                "Elegí una opción",
                                            )}`}
                                        />
                                        <Field
                                            component={Selector}
                                            idForm={FORM_ID}
                                            name="locality"
                                            options={localityList}
                                            label={`${i18nUtils.get("Localidad", "Localidad")}`}
                                            placeholder={`${i18nUtils.get(
                                                "environment.step.beneficiaries.locality.placeholder",
                                                "Elegí una opción",
                                            )}`}
                                            disabled={fetchingLocalities}
                                            onChange={onChangeLocality}
                                        />
                                        <Field
                                            component={TextField}
                                            idForm={FORM_ID}
                                            name="street"
                                            labelTextDefault={`${i18nUtils.get(
                                                "onboarding.personalData.street.label",
                                                "Calle",
                                            )}`}
                                            hidePlaceholder
                                        />
                                        <Field
                                            component={TextField}
                                            idForm={FORM_ID}
                                            name="streetNumber"
                                            labelTextDefault={`${i18nUtils.get(
                                                "onboarding.personalData.streetNumber.label",
                                                "Número",
                                            )}`}
                                            hidePlaceholder
                                        />
                                        <Field
                                            component={TextField}
                                            idForm={FORM_ID}
                                            name="floor"
                                            labelTextDefault={`${i18nUtils.get(
                                                "onboarding.personalData.floor.label",
                                                "Piso",
                                            )}`}
                                            hidePlaceholder
                                        />
                                        <Field
                                            component={TextField}
                                            idForm={FORM_ID}
                                            name="apartment"
                                            labelTextDefault={`${i18nUtils.get(
                                                "onboarding.personalData.apartment.label",
                                                "Departamento",
                                            )}`}
                                            hidePlaceholder
                                        />
                                        <Field
                                            component={TextField}
                                            idForm={FORM_ID}
                                            name="postalCode"
                                            labelTextDefault={`${i18nUtils.get(
                                                "onboarding.personalData.postalCode.label",
                                                "Código postal",
                                            )}`}
                                            hidePlaceholder
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="grid-table form-actions">
                                <div className="grid-table-row">
                                    <div className="grid-table-data">
                                        <Button
                                            bsStyle="primary"
                                            label={verifiedUser ? "global.add" : "onboarding.cuil.search.button"}
                                            type="submit"
                                            block
                                            disabled={!submitIsAllowed(allFormik)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Form>
                    </div>
                </Drawer>
            )}
        </Formik>
    );
};

EnvironmentBeneficiarySlide.propTypes = {
    isMobile: bool.isRequired,
    page: string.isRequired,
    slideIsVisible: bool.isRequired,
    closeSlide: func.isRequired,
    onSubmit: func.isRequired,
    countryList: arrayOf(shape({})).isRequired,
    pepJobList: arrayOf(shape({})).isRequired,
    pepRelationshipTypeList: arrayOf(shape({})).isRequired,
    stateList: arrayOf(shape({})),
    localityList: arrayOf(shape({})),
    onChangeCountry: func.isRequired,
    onChangeState: func.isRequired,
    fetchingStates: bool.isRequired,
    fetchingLocalities: bool.isRequired,
    shareholders: arrayOf(shape({})).isRequired,

    maritalStatusList: arrayOf(shape({})).isRequired,
    occupationList: arrayOf(shape({})).isRequired,
    foreignCountries: arrayOf(shape({})).isRequired,
    genders: arrayOf(shape({})).isRequired,
    nationalities: arrayOf(shape({})).isRequired,
};

EnvironmentBeneficiarySlide.defaultProps = {
    stateList: [],
    localityList: [],
};

const mapStateToProps = (state) => ({
    maritalStatusList: onboardingSelectors.getMaritalStatusList(state),
    occupationList: onboardingSelectors.getOccupationList(state),
    stateList: onboardingSelectors.getStateList(state),
    localityList: onboardingSelectors.getLocalityList(state),
    foreignCountries: onboardingSelectors.getForeignCountries(state),
    genders: onboardingSelectors.getGenders(state),
    nationalities: onboardingSelectors.getNationalities(state),
});

export default connect(mapStateToProps)(EnvironmentBeneficiarySlide);
